import React, { useRef, useEffect, useState, useCallback, memo } from 'react';
import Table from '@/components/organisms/project-builder/table';
import Typography from '@/components/atoms/project-builder/typography';
import Button from '@/components/atoms/project-builder/button';
import Popup from '@/components/atoms/project-builder/popup';
import { tablet, desktop } from '@/lib/utils/screensizes';
import useMediaQuery from '@/hooks/useMediaQuery';
import useProjectBuilder from '@/lib/hooks/useProjectBuilder';
import Spinner from '@/components/organisms/spinner';
import { FaCheck } from 'react-icons/fa';
import { InformationCircleIcon } from '@heroicons/react/24/outline';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors, TouchSensor } from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy, arrayMove } from '@dnd-kit/sortable';
import { restrictToFirstScrollableAncestor, restrictToParentElement, restrictToVerticalAxis, restrictToWindowEdges } from '@dnd-kit/modifiers';
import { CSS } from '@dnd-kit/utilities';
import SaveConfirmationPopup from './save-confirmation-popup';
import { Tooltip } from 'react-tooltip';
import { getLifecycleBadge } from '@/utils/product-lifecycle';
import { useParams } from 'next/navigation';
import { checkIsBuilderProduct } from '@/helpers/builder-product-helper';
interface Variant {
  id: string;
  sku: string;
  images: string[];
  attributes: Record<string, any>;
  price: {
    fractionDigits: number;
    centAmount: number;
    currencyCode: string;
  };
}
interface Item {
  id: string;
  type: string;
  productId: string;
  name: string;
  quantity: number;
  roomName: string;
  roomNotes: string;
  variant: Variant;
  isProductAvailable?: boolean;
}
interface Section {
  roomName: string;
  items: Item[];
  showPricesOnPdf: boolean;
}
interface ProjectFormData {
  sections: Section[];
}
interface ProjectFormDataProps {
  projectForm: ProjectFormData,
  handleTabClick: (tabIndex: number, formData: any) => void;
}
interface ConfirmationPopupProps {
  isOpen: boolean;
  selectedRoom: any;
  identifier: string,
}

interface ButtonProps {
  item: object,
  sectionIndex: number,
  identifier: string;
}
interface ItemQuantityProps {
  item: any,
  sectionIndex: number,
  handleQuantityChange: Function,
}
interface PriceDetailsProps {
  item: any,
}
interface Payload {
  lineITemsToUpdate?: any[];
  lineItemsToRemove?: any[];
  roomsAfterRemoval?: any[];
  lineItemPriorityToUpdate?: any[];
  version: number;
  projectId: string;
  roomGroups?: any[];
  allRoomsAreDeleted?: boolean;
}
interface EditConfirmationPopupProps {
  isOpen: boolean;
  selectedRoom?: { name: string; };
  onConfirm: () => void;
  onCancel: () => void;
  isLoading?: boolean;
  successMessage?: string;
  roomAlreadyExists?: boolean;
}
const ViewProducts: React.FC<any> = ({ projectForm, handleTabClick, handleConfirmLeave, handleCancelLeave,
  setIsFormDirty, openSaveConfirmation, setOpenSaveConfirmation, tabIndexValue, setTabIndexValue, selectDataForPublishedProducts }: any) => {

  const params = useParams();
  const locale = (params?.locale as string) || 'au';

  const projectFormData = projectForm || projectForm?.data;
  // console.log('prodata',projectFormData);
  const handleButtonClick = () => {
      handleTabClick(2, projectFormData);
    };
  if (!projectFormData || !projectFormData?.roomGroups?.length) {

    return (
      <div className="">
        <div className="mb-5"> Browse the website to start adding products to a project. </div>
        <div className="flex h-72 w-60 items-center justify-center border-2 border-dotted border-gray-400">
          <button
            onClick={handleButtonClick}
            className="fontSize14-sm items-center justify-center rounded-full border bg-orange-100 px-[18px] py-[4px]"
          >
            Add products
          </button>
        </div>
      </div>
    );
  }

  const [projectFormDataState, setProjectFormData] = useState<any>(projectFormData);
  const { performMutationOnMyProjectById, updateProjectStatus, updateShoppingList, getProjectById } = useProjectBuilder();
  const [sections, setSections] = useState<Section[]>(projectFormDataState.roomGroups);
  const [selectedItem, setSelectedItem] = useState({});
  const [identifier, setIdentifier] = useState("");
  const [sectionIndexValue, setSectionIndexValue] = useState(0);
  const [openPopup, setOpenPopup] = useState<boolean>(false);
  const [isTablet] = useMediaQuery(tablet);
  const [isDesktop] = useMediaQuery(desktop);
  const [browserWidth] = useMediaQuery();

  const [lineItemsToRemove, setLineItemsToRemove] = useState<string[]>([]);
  const [roomsAfterRemoval, setRoomsAfterRemoval] = useState<string[]>([]);
  const [roomsRemovalLineItem, setRoomsRemovalLineItem] = useState<string[]>([]);
  const [lineItemsPriorityToUpdate, setLineItemsPriorityToUpdate] = useState<any>();
  const [formData, setFormData] = useState(null);
  const [navigationToDiffStepperid, setNavigationToDiffStepperid] = useState(0);
  const [discardClicked, setDiscardClicked] = useState(false);
  const [localFormNavigation, setLocalFormNavigation] = useState(false);
  const [lineItemsToUpdate, setLineItemsToUpdate] = useState<
    { id: string; quantity: number; }[]
  >([]);
  const [loading, setLoading] = useState<any>(false);
  const isFormDirtyRef = useRef(false);

  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editedRoomName, setEditedRoomName] = useState('');
  const [showEditedRoom, setShowEditedRoom] = useState(false);
  const [showConfirmPopup, setShowConfirmPopup] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const [updateSuccessMessage, setUpdateSuccessMessage] = useState('');
  const [currentlyEditingId, setCurrentlyEditingId] = useState<string | null>(null);
  const [inputWidth, setInputWidth] = useState(40);
  const spanRef = useRef<HTMLSpanElement>(null);
  const initialQuantitiesRef = useRef<Map<string, number>>(new Map());
   const [isHydrated, setIsHydrated] = useState(false);
  const [pendingNotesData, setPendingNotesData] = useState<{notes: string, item: any, sectionIndex: number} | null>(null);
  useEffect(() => {
    if(isFormDirtyRef.current === false){
      setSections(projectFormDataState.roomGroups || []);
    }
  }, [projectFormDataState]);
  useEffect(() => {
    if (projectForm) {
      setProjectFormData(projectForm);
    }
  }, [projectForm]);

  const addOrUpdateItemInLineItemsToUpdate = (id: string, quantity: number) => {
    setLineItemsToUpdate((prevItems) => {
      const existingIndex = prevItems.findIndex((item) => item.id === id);
      if (existingIndex !== -1) {
        const updatedItems = [...prevItems];
        updatedItems[existingIndex].quantity = quantity;
        return updatedItems;
      } else {
        return [...prevItems, { id, quantity }];
      }
    });
  };

  const handleDelete = (sectionIndex: number, itemId: string) => {
    setIsFormDirty(true);
    isFormDirtyRef.current = true;
    setSections((prevSections) => {
      const updatedSections = prevSections
        .map((section, idx) => {
          if (idx === sectionIndex) {
            const updatedItems = section.items.filter((item) => item.id !== itemId);
            if (updatedItems.length === 0) {
              setRoomsRemovalLineItem((prevItems) => {
                return [...prevItems, section.roomName];
              });
              return null;
            }
            return { ...section, items: updatedItems };
          }
          return section;
        })
        .filter((section): section is Section => section !== null);
      return updatedSections;
    });

    setLineItemsToRemove((prevItems) => {
      if (!prevItems.includes(itemId)) {
        return [...prevItems, itemId];
      }
      return prevItems;
    });
    setOpenPopup(false);
  };

  const handleDeleteSection = (sectionIndex: number, selectedRoom: any) => {
    setIsFormDirty(true);
    isFormDirtyRef.current = true;
    setSections((prevSections) => prevSections.filter((_, idx) => idx !== sectionIndex));
    setRoomsAfterRemoval((prevItems) => {
      return [...prevItems, selectedRoom.name];
    });

    setOpenPopup(false);
  };

  useEffect(() => {
  if (projectFormDataState) {
    const quantityMap = new Map<string, number>();
    projectFormDataState.roomGroups.forEach((section: Section) => {
      section.items.forEach(item => {
        quantityMap.set(item.id, item.quantity);
      });
    });
    initialQuantitiesRef.current = quantityMap;
  }
}, [projectFormDataState]);


  const handleQuantityChange = useCallback((
    sectionIndex: number,
    id: string,
    action: "increase" | "decrease" | "set",
    value?: number
  ) => {
    setIsFormDirty(true);
    isFormDirtyRef.current = true;
    setSections((prevSections) => {
      let targetItem: Item | null = null;
      let targetSection: Section | null = null;
      
      for (const section of prevSections) {
        const item = section.items.find((it) => it.id === id);
        if (item) {
          targetItem = item;
          targetSection = section;
          break;
        }
      }

      if (!targetItem || !targetSection) {
        return prevSections;
      }

      let newQuantity = targetItem.quantity;
      if (action === "increase") {
        newQuantity += 1;
      } else if (action === "decrease") {
        newQuantity = Math.max(1, targetItem.quantity - 1);
      } else if (action === "set" && typeof value === "number" && value > 0) {
        newQuantity = value;
      }

      const itemSku = (targetItem.variant?.sku || '').toUpperCase().trim();
      let itemRoom = String(targetSection.roomName || '').trim();
      
      if (itemRoom && !/\d+$/.test(itemRoom)) {
        itemRoom = `${itemRoom} 1`;
      }

      if (itemSku) {
        let totalQuantity = 0;
        prevSections.forEach((section) => {
          let sectionRoom = String(section.roomName || '').trim();
          if (sectionRoom && !/\d+$/.test(sectionRoom)) {
            sectionRoom = `${sectionRoom} 1`;
          }
          
          if (sectionRoom === itemRoom) {
            section.items.forEach((it) => {
              const itSku = (it.variant?.sku || '').toUpperCase().trim();
              if (itSku === itemSku) {
                if (it.id === id) {
                  totalQuantity += newQuantity;
                } else {
                  totalQuantity += it.quantity;
                }
              }
            });
          }
        });

        if (totalQuantity > 9999) {
          return prevSections;
        }
      }

      return prevSections.map((section, idx) =>
        idx === sectionIndex
          ? {
              ...section,
              items: section.items.map((item) => {
                if (item.id === id) {
                  if (newQuantity !== item.quantity) {
                    addOrUpdateItemInLineItemsToUpdate(id, newQuantity);
                    return { ...item, quantity: newQuantity };
                  }
                }
                return item;
              }),
            }
          : section
      );
    });
  }, []);

  const getExcludedRoomIds = (
    parent: any,
    deletedRooms: string[]
  ): string[] => {
    const excludedRooms = deletedRooms.length === 0
      ? parent
      : parent.filter((room: any) => deletedRooms.includes(room.roomName));
    const excludedIds = excludedRooms.flatMap((room: any) =>
      room.items.map((item: any) => item.id)
    );
    return excludedIds;
  };

  const capitalizeFirstLetter = (str: string) => str?.charAt(0)?.toUpperCase() + str?.slice(1);
  const ctDataToFormData = (ctData: any) => {
    const { projectLocation, customerDetails, projectType, status, roomLabels } = ctData;
    const roomDetailsObj: any = {},
      selectedRoomDetails: any = [];
    roomLabels?.forEach((e: any) => {
      const roomCount = parseInt(e.at(-1));
      const roomName = isNaN(roomCount) ? e.trim() : e.slice(0, e.length - 1).trim();
      if (isNaN(roomCount)) {
        roomDetailsObj[roomName] = 1;
      } else if (!roomDetailsObj[roomName] || roomCount > roomDetailsObj[roomName]) {
        roomDetailsObj[roomName] = roomCount;
      }
    });

    Object.keys(roomDetailsObj).map((e) => {
      selectedRoomDetails.push({
        roomName: e,
        count: roomDetailsObj[e],
      });
    });
    return {
      ...ctData,
      projectLocation: {
        name: projectLocation,
        value: projectLocation,
      },
      projectType: {
        name: capitalizeFirstLetter(projectType),
        value: projectType,
      },
      projectStatus: {
        name: capitalizeFirstLetter(status),
        value: status,
      },
      selectedRoomDetails,
      customerEmail: customerDetails?.email,
      customerName: customerDetails?.name,
      customerPhone: customerDetails?.phone,
      customerAddress:customerDetails?.customerAddress || ''
    };
  };

  const cleanLineItems=(payload:any) =>{
  const { lineItemsToRemove, lineITemsToUpdate, lineItemPriorityToUpdate } = payload;

  const updatedLineItemsToUpdate = lineITemsToUpdate?.filter(
    (item:any) => !lineItemsToRemove?.includes(item.lineItemId)
  );

  const updatedLineItemPriorityToUpdate = lineItemPriorityToUpdate?.filter(
    (item:any) => !lineItemsToRemove?.includes(item.lineItemId)
  );

  return {
    ...payload,
    lineITemsToUpdate: updatedLineItemsToUpdate,
    lineItemPriorityToUpdate: updatedLineItemPriorityToUpdate,
  };
}


  const handleSubmit = async (navId = 0) => {
    setIsFormDirty(false);
    setLocalFormNavigation(false);
    setOpenSaveConfirmation(false);
    const payload: Payload = {
      version: projectFormDataState.version,
      projectId: projectFormDataState.shoppingListId
    };

    const changedItems: { lineItemId: string; quantity: number; }[] = [];
    const initialQuantities = initialQuantitiesRef.current;

    sections.forEach(section => {
      section.items.forEach(item => {
        const initialQty = initialQuantities.get(item.id);
        if (initialQty !== undefined && initialQty !== item.quantity) {
          changedItems.push({
            lineItemId: item.id,
            quantity: item.quantity
          });
        }
      });
    });
    payload.lineITemsToUpdate = changedItems;

    sections.forEach((item) => {
      item.items.forEach((value) => {
        if (lineItemsToRemove.includes(value.id)) {
          setLineItemsToRemove((prevItems) =>
            prevItems.filter((id) => id !== value.id)
          );
        }
      });
    });
    if (lineItemsToRemove.length > 0) {
      payload.lineItemsToRemove = lineItemsToRemove;
    }

    if (projectFormDataState?.roomGroups?.length !== sections.length) {
      const deletedRooms = [...new Set([...roomsAfterRemoval, ...roomsRemovalLineItem])];
      if (deletedRooms.length > 0) {
        payload.roomsAfterRemoval = projectFormDataState?.roomLabels?.filter((item: any) => !deletedRooms.includes(item));
        const excludedIds = getExcludedRoomIds(projectFormDataState?.roomGroups, deletedRooms);
        const concatIds = [...new Set([...lineItemsToRemove, ...excludedIds])];
        payload.lineItemsToRemove = concatIds;
      }
      if (deletedRooms?.length > 0 && payload?.roomsAfterRemoval?.length === 0) {
        payload.allRoomsAreDeleted = true;
      }
    }
    if (lineItemsPriorityToUpdate?.length > 0) {
      payload.lineItemPriorityToUpdate = lineItemsPriorityToUpdate.map((item:any) => ({
        lineItemId: item.id,
        itemPriority: item.value,
      }));
    }
    try {
      setLoading(true)
      if(!isFormDirtyRef.current){
        navigationToDiffStepper(4);
        return;
      }
      const result: any = await performMutationOnMyProjectById(cleanLineItems(payload));
      if (result?.shoppingList) {
        const newFormData = ctDataToFormData(result.shoppingList);
        setFormData(newFormData);
        handleConfirmLeave();
        if (navId !== 0) {
          setNavigationToDiffStepperid(navId);
        } else {
          setNavigationToDiffStepperid(tabIndexValue);
        }
        setDiscardClicked(true);
      }else{
        navigationToDiffStepper(4)
      }
      setLoading(false);
    } catch (error) {
      console.log('-error-:', error);
      handleCancelLeave();
      setIsFormDirty(true);
      setLoading(false);
    }
  };

  const navigationToDiffStepper = (id: number) => {
    setNavigationToDiffStepperid(id);
    setTabIndexValue(id);
    if (isFormDirtyRef.current) {
      setLocalFormNavigation(true);
      setOpenSaveConfirmation(true);
      return;
    }
    setTimeout(() => {
      setFormData(projectFormDataState);
      setOpenSaveConfirmation(false);
      setDiscardClicked(true);
    }, 0);
  };

  const openPopupDialog = (item: any, identifier: string, index: number) => {
    setOpenPopup(true);
    setSelectedItem(item);
    setIdentifier(identifier);
    setSectionIndexValue(index);
    setShowEditedRoom(false);
  };
  const handlePopupClose = () => {
    setOpenPopup(false);
  };
  useEffect(() => {

    if (discardClicked && !localFormNavigation) {
      if (formData) {
        setIsFormDirty(false);
        handleTabClick(navigationToDiffStepperid, formData);
      } else {
        handleTabClick(tabIndexValue, projectFormDataState);
      }
    }
  }, [discardClicked, formData, localFormNavigation]);

  const discardConfirmation = () => {
    setIsFormDirty(false);
    setLocalFormNavigation(false);
    setDiscardClicked(true);
    setOpenSaveConfirmation(false);
    setPendingNotesData(null);
    setCurrentlyEditingId(null);
    setTimeout(() => {
      handleConfirmLeave();
    }, 100);
  };


  const closeConfirmation = () => {
    setTabIndexValue(3);
    setOpenSaveConfirmation(false);
    handleCancelLeave();
  };

  const handleSaveAndContinue = async () => {
    try {
      setLoading(true);
      // First, save pending notes if any
      if (pendingNotesData) {
        await onUpdateNotes(pendingNotesData.notes, pendingNotesData.item);
        // Update sections state to reflect the saved notes
        setSections((prevSections: any) =>
          prevSections.map((section: any, idx: number) =>
            idx === pendingNotesData.sectionIndex
              ? {
                  ...section,
                  items: section.items.map((it: any) =>
                    it.id === pendingNotesData.item.id ? { ...it, roomNotes: pendingNotesData.notes } : it
                  ),
                }
              : section
          )
        );
        setPendingNotesData(null);
        setCurrentlyEditingId(null);
      }

      const changedItems: { lineItemId: string; quantity: number; }[] = [];
      const initialQuantities = initialQuantitiesRef.current;

      sections.forEach(section => {
        section.items.forEach(item => {
          const initialQty = initialQuantities.get(item.id);
          if (initialQty !== undefined && initialQty !== item.quantity) {
            changedItems.push({
              lineItemId: item.id,
              quantity: item.quantity
            });
          }
        });
      });

      const hasQuantityChanges = changedItems.length > 0;
      const hasLineItemRemovals = lineItemsToRemove.length > 0;
      const hasRoomRemovals = projectFormDataState?.roomGroups?.length !== sections.length;
      const hasPriorityUpdates = lineItemsPriorityToUpdate?.length > 0;

      // Only call handleSubmit if there are actual changes beyond notes
      if (hasQuantityChanges || hasLineItemRemovals || hasRoomRemovals || hasPriorityUpdates) {
        await handleSubmit();
      } else {
        setIsFormDirty(false);
        isFormDirtyRef.current = false;
        setOpenSaveConfirmation(false);
        handleConfirmLeave();
        setDiscardClicked(true);
        if (tabIndexValue !== 0) {
          setNavigationToDiffStepperid(tabIndexValue);
        }
        setLoading(false);
      }
    } catch (error) {
      console.error('Error saving notes and continuing:', error);
      setLoading(false);
    }
  };

  const ConfirmationPopup: React.FC<ConfirmationPopupProps> = ({ isOpen, selectedRoom, identifier }) => {
    if (!isOpen) return null;
    return (
      <Popup isOpen={isOpen} bodyClassNames="sm:mx-unset max-w-[380px]">
        <div className="break-words p-[25px]">
          <p className="mt-[27px] whitespace-normal text-gray-700">
            Are you sure you want to delete <br /> <strong>{selectedRoom?.name}</strong>
          </p>
          <div className="mt-[51px] flex gap-[36px] space-x-[4px]">
            <Button
              variant={'secondary'}
              className="md:whitespace-nowrap cursor-pointer !border-[#dee2e6] !px-[27px] !py-[9px] text-sm font-semibold"
              type="button"
              onClick={() =>
                identifier == 'parent'
                  ? handleDeleteSection(sectionIndexValue, selectedRoom)
                  : handleDelete(sectionIndexValue, selectedRoom.id)
              }
            >
              Yes, please delete
            </Button>

            <Button
              variant={'secondary'}
              className="whitespace-nowrap cursor-pointer !border-[#dee2e6] !px-[27px] !py-[9px] text-sm font-semibold"
              type="button"
              onClick={handlePopupClose}
            >
              Cancel
            </Button>
          </div>
        </div>
      </Popup>
    );
  };
  const OpenDialogButton: React.FC<ButtonProps> = ({ item, sectionIndex, identifier }) => {
    return (
      <div
        onClick={(e) => {
          e.stopPropagation();
          openPopupDialog(item, identifier, sectionIndex);
        }}
        onPointerDown={(e) => e.stopPropagation()}

      >
        <Button variant="ghost" className="!p-0">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={1.5}
            stroke="currentColor"
            className="h-[20px] w-[20px] cursor-pointer text-red-600 hover:text-red-800"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="m20.25 7.5-.625 10.632a2.25 2.25 0 0 1-2.247 2.118H6.622a2.25 2.25 0 0 1-2.247-2.118L3.75 7.5m6
                                                    4.125 2.25 2.25m0 0 2.25 2.25M12 13.875l2.25-2.25M12 13.875l-2.25 2.25M3.375 7.5h17.25c.621 0 1.125-.504
                                                    1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125H3.375c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125Z"
            />
          </svg>
        </Button>
      </div>
    );
  };

  const PriceDetails: React.FC<PriceDetailsProps> = ({ item }) => {
    console.log(item, 'item');
    const isBuilderProduct = checkIsBuilderProduct(item, selectDataForPublishedProducts, locale);
    if (isBuilderProduct) {
      return null;
    }
    return (
      <div>
        <Typography className="whitespace-normal text-18">
          {`$${((item?.variant?.price?.centAmount * item.quantity) / 100).toFixed(
            item?.variant?.price?.fractionDigits,
          )}`}
        </Typography>
      </div>
    );
  };
  const [checked, setChecked] = useState(projectFormDataState?.showPricesOnPdf || false);
  const handleToggle = async () => {
    try {
      setChecked(!checked);
      const payload = {
        shoppingListId: projectFormDataState?.shoppingListId,
        version: projectFormDataState?.version,
        status: !checked,
        fieldName: 'showPricesOnPdf',
      };
      await updateProjectStatus(payload);
    } catch (error) {
      console.log('error:-', error);
    }
  };

  const EditConfirmationPopup: React.FC<EditConfirmationPopupProps> = ({
    isOpen,
    selectedRoom,
    onConfirm,
    onCancel,
    isLoading = false,
    successMessage = '',
    roomAlreadyExists
  }) => {
    if (!isOpen) return null;

    return (
      <Popup isOpen={isOpen} bodyClassNames="sm:mx-unset max-w-[380px]">
        <div className="break-words p-[25px]">
          <p
            className="mt-[27px] whitespace-normal text-gray-700"
          >
            Are you sure you want to update the room name to
            <strong> {(selectedRoom?.name || "")}</strong>
          </p>
          {roomAlreadyExists ? (
            <p
              className="mt-[27px] whitespace-normal text-gray-700"
            >
              Giving an existing room name will move all products under the existing room.
            </p>
          ) : ''}

          {successMessage && <p className="mt-[4px] font-semibold text-green-600">{successMessage}</p>}
          <div className="mt-[51px] flex gap-[31px] space-x-[4px]">
            <Button
              variant="secondary"
              className="cursor-pointer !border-[#dee2e6] !px-[27px] !py-[9px] text-sm font-semibold"
              type="button"
              onClick={onConfirm}
              disabled={isLoading}
            >
              {isLoading ? 'Updating...' : 'Yes, update'}
            </Button>
            <Button
              variant="secondary"
              className="cursor-pointer !border-[#dee2e6] !px-[27px] !py-[9px] text-sm font-semibold"
              type="button"
              onClick={onCancel}
              disabled={isLoading}
            >
              Cancel
            </Button>
          </div>
        </div>
      </Popup>
    );
  };
  const handleEditClick = (index: number, currentName: string) => {
    setEditingIndex(index);
    setEditedRoomName(currentName);
    setShowEditedRoom(true);
  };
  useEffect(() => {
    if (inputRef.current && editingIndex !== null) {
      const input = inputRef.current;
      input.focus();

      const length = input.value.length;
      input.setSelectionRange(length, length);
    }
  }, [editingIndex]);

  const handleCancelEdit = () => {
    setEditingIndex(null);
    setEditedRoomName('');
    setShowEditedRoom(false);
  };

  const handleRoomNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const regex = /[^a-zA-Z0-9\s-]/g;
    const sanitizedValue = e.target.value.replace(regex, '');
    setEditedRoomName(sanitizedValue);
  };

  const handleCheckClick = () => {
    setShowConfirmPopup(true);
  };

  const isSameItem = (item1: any, item2: any) => {
    const sanitize = (item: any) => {
      const { id, roomName, roomNotes, quantity, ...rest } = item;
      return rest;
    };

    const sanitized1 = sanitize(item1);
    const sanitized2 = sanitize(item2);

    return JSON.stringify(sanitized1) === JSON.stringify(sanitized2);
  };

  const trimmedRoomName = editedRoomName.trim();
  const roomAlreadyExists = sections.some(
    (section, index) => (section.roomName.trim())?.toLowerCase() === trimmedRoomName?.toLowerCase() && index !== editingIndex
  );
  const toTitleCase = (str: string): string => {
    return str
      .toLowerCase()
      .split(' ')
      .filter(word => word.trim() !== '')
      .map(word => word[0].toUpperCase() + word.slice(1))
      .join(' ');
  };

  const handleConfirmUpdate = async () => {
    if (editingIndex === null) return;

    const trimmedRoomName = editedRoomName.trim();
    if (!trimmedRoomName) return;

    setIsUpdating(true);
    setUpdateSuccessMessage('');


    const updatedSections = [...sections];
    const sectionToUpdate = updatedSections[editingIndex];
    const previousRoomName = sectionToUpdate.roomName;

    const updatedItems = sectionToUpdate.items.map((item) => ({
      ...item,
      roomName: (trimmedRoomName),
    }));

    const existingIndex = updatedSections.findIndex(
      (section, index) => (section.roomName.trim())?.toLowerCase() === trimmedRoomName?.toLowerCase() && index !== editingIndex,
    );

    const removeId: any = [];
    if (existingIndex !== -1) {
      const existingSection = updatedSections[existingIndex];


      updatedItems.forEach(newItem => {
        const matchIndex = existingSection.items.findIndex(existingItem =>
          isSameItem(existingItem, newItem)
        );
        if (matchIndex !== -1) {
          removeId.push(newItem.id);
          existingSection.items[matchIndex].quantity += newItem.quantity;
        } else {
          existingSection.items.push(newItem);
        }
      });
      updatedSections.splice(editingIndex, 1);
    } else {
      updatedSections[editingIndex] = {
        ...sectionToUpdate,
        roomName: (trimmedRoomName),
        items: updatedItems,
      };
    }
    const updatedRoomNames = (projectFormDataState?.roomLabels??[])?.map((name: string) =>
      name?.toLowerCase() === previousRoomName?.toLowerCase() ? (trimmedRoomName) : name
    );

    const updateQuantity: { lineItemId: any; quantity: any; }[] = [];
    if (removeId.length) {
      updatedSections.forEach((value: any) => {
        value.items.forEach((element: any) => {
          updateQuantity.push({
            lineItemId: element.id,
            quantity: element.quantity
          });
        });
      });
    }

    const lineItemsToUpdate = updatedItems
      .filter(item => !removeId.includes(item.id))
      .map(item => ({
        lineItemId: item.id,
        roomName: (item.roomName),
      }));

    const payload = {
      roomsAfterRemoval: [...new Set(updatedRoomNames)],
      lineItems: lineItemsToUpdate,
      projectId: projectFormDataState.shoppingListId,
      version: projectFormDataState.version,
      updateRoomName: true,
      lineItemsToRemove: removeId,
      lineITemsToUpdate: updateQuantity
    };
    try {
      const response = await performMutationOnMyProjectById(cleanLineItems(payload));
      const projectData: any = await getProjectById({ shoppingListId: projectFormDataState?.shoppingListId });
      const updatedList = ctDataToFormData(projectData?.data);
      setProjectFormData(updatedList);
      setSections(updatedSections);

      setUpdateSuccessMessage('Room updated successfully!');
    } catch (err) {
      console.error("Failed to update room:", err);
    }

    setIsUpdating(false);
    setTimeout(() => {
      setShowConfirmPopup(false);
      setEditingIndex(null);
      setUpdateSuccessMessage('');
      setShowEditedRoom(false);
    }, 1500);
  };
  const sensors = useSensors(useSensor(PointerSensor));
  const mobileSensors = useSensors(
    useSensor(TouchSensor, {
      activationConstraint: {
        distance: 10,
      },
    }),
  );
  const lockScroll = () => {
    if (typeof window !== 'undefined') {
      document.body.style.overflow = 'hidden';
    }
  };

  const unlockScroll = () => {
    if (typeof window !== 'undefined') {
      document.body.style.overflow = '';
    }
  };


  const onUpdateNotes = async (notes: any, item: any) => {
    try {
      const payload = {
        roomNotes: notes,
        projectId: projectFormDataState.shoppingListId,
        version: projectFormDataState.version,
        lineItemId: item.id,
        updateRoomNotes: true
      };
      const response = await performMutationOnMyProjectById(payload);
    } catch (error) {
      console.log('error updating notes:', error);
    }
    /* Need to fix as it's resetting the quantity*/
    /* const projectData: any = await getProjectById({ shoppingListId: projectFormDataState?.shoppingListId });
    const updatedList = ctDataToFormData(projectData?.data);
    setProjectFormData(updatedList);
    setSections(updatedList.roomGroups); */
  };

  useEffect(() => {
    if (spanRef.current) {
      const spanWidth = spanRef.current.offsetWidth;
      setInputWidth(spanWidth + 20);
    }
  }, [editedRoomName]);


  useEffect(() => {
    setIsHydrated(true);
  }, []);



  if (loading || !isHydrated) {
    return <>
    <Spinner />
    <div className="overlay fixed left-0 top-0 z-50 h-full w-full bg-gray-800 opacity-75"></div>
    </>;
  }

  return (
    <div className="p-[6px] md:p-[10px]">
      <div className="flex justify-between md:justify-end mt-[15px] mb-[25px] md:mt-[20px] md:mb-[30px]">
        <span>Display prices on pdf</span>
        <label className="relative ml-[10px] inline-flex cursor-pointer items-center">
          <input type="checkbox" checked={checked} onChange={handleToggle} className="sr-only" />
          <div className={`h-[24px] w-[44px] rounded-full transition ${checked ? 'bg-[#2B2C39]' : 'bg-gray-300'}`}>
            <div
              className={`absolute left-[2px] top-[4px] flex h-[20px] w-[20px] items-center justify-center rounded-full
                            bg-white transition-transform ${checked ? 'translate-x-[20px]' : ''}`}
            >
              {checked && <FaCheck className="h-[10px] text-3xl text-gray-500" />}
            </div>
          </div>
        </label>
      </div>
      <div className='h-[800px] xl:h-[500px] overflow-auto'>
        <>
          {sections.map((section, sectionIndex) => {
            const handleDragEnd = (event: any) => {
              unlockScroll();
              setIsFormDirty(true);
              isFormDirtyRef.current = true;

              const { active, over } = event;
              if (!over) return;

              if (active?.id !== over?.id) {
                const oldIndex = section.items.findIndex((item) => item.id === active?.id);
                const newIndex = section.items.findIndex((item) => item.id === over?.id);

                const newItems = arrayMove(section.items, oldIndex, newIndex);

                // Update UI
                const updatedSections = [...sections];
                updatedSections[sectionIndex].items = newItems;
                setSections(updatedSections);

                // Push all items with their new positions
                const updatedPriorities = newItems.map((item, index) => ({
                  id: item.id,
                  value: index,
                }));
                setLineItemsPriorityToUpdate((prev: any) => {
                  const prevItems = Array.isArray(prev) ? prev : [];
                  const updatedMap = new Map(prevItems.map((item: any) => [item.id, item.value]));

                  newItems.forEach((item: any, index: number) => {
                    updatedMap.set(item.id, index);
                  });

                  return Array.from(updatedMap.entries()).map(([id, value]) => ({ id, value }));
                });

              }
            };

            return (
              <div key={section.roomName} className="mb-[12px] overflow-y-hidden overflow-x-auto">
                <div className="mb-[27px] mr-[10px] flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    {editingIndex === sectionIndex && showEditedRoom ? (
                      <>
                        <span
                          ref={spanRef}
                          className="absolute invisible whitespace-pre text-40 px-2 py-1"
                        >
                          {editedRoomName || ' '}
                        </span>
                        <input
                          ref={inputRef}
                          type="text"
                          value={editedRoomName}
                          onChange={handleRoomNameChange}
                          maxLength={30}
                          style={{ width: `${inputWidth}px` }}
                          className="rounded border border-gray-300 px-2 py-1 text-40
                          max-w-40 sm:max-w-75 lg:max-w-none
                          w-full sm:w-auto"
                        />
                        <svg
                          onClick={handleCheckClick}
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          strokeWidth={1.5}
                          stroke="currentColor"
                          className="h-5 w-5 cursor-pointer text-green-600"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" d="m4.5 12.75 6 6 9-13.5" />
                        </svg>
                        <svg
                          onClick={handleCancelEdit}
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          strokeWidth={1.5}
                          stroke="currentColor"
                          className="h-5 w-5 cursor-pointer text-red-500"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
                        </svg>
                      </>
                    ) : (
                      <>
                        <span className="max-w-60 whitespace-normal break-words text-40 sm:max-w-none">
                          {section.roomName}
                        </span>
                      </>
                    )}
                  </span>
                  <div className="flex gap-[10px]">
                    <svg
                      onClick={() => handleEditClick(sectionIndex, section.roomName)}
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth={1.5}
                      stroke="currentColor"
                      className="ml-[5px] h-[18px] w-[18px] cursor-pointer"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5
                    0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125M18 14v4.75A2.25
                    2.25 0 0 1 15.75 21H5.25A2.25 2.25 0 0 1 3 18.75V8.25A2.25 2.25 0 0 1 5.25 6H10"
                      />
                    </svg>
                    <OpenDialogButton item={{ name: section.roomName }} sectionIndex={sectionIndex} identifier="parent" />
                  </div>
                </div>
                <div>
                  {isDesktop && (
                    <DndContext
                      sensors={browserWidth === 1024 || browserWidth === 834 ? mobileSensors : sensors}
                      onDragStart={() => (browserWidth === 1024 || browserWidth === 834 ? lockScroll() : null)}
                      onDragEnd={handleDragEnd}
                      modifiers={
                        browserWidth === 1024 || browserWidth === 834
                          ? [restrictToVerticalAxis, restrictToFirstScrollableAncestor]
                          : [restrictToVerticalAxis, restrictToWindowEdges, restrictToParentElement]
                      }
                    >
                      <Table.Container className="w-full max-w-full table-fixed border-0 border-t-0">
                        <Table.Head>
                          <Table.Row>
                            <Table.Cell className="w-[10%]">
                              <Typography className="text-[16px]">Product Name</Typography>
                            </Table.Cell>
                            <Table.Cell className="w-[40%]"></Table.Cell>
                            <Table.Cell className="w-[15%]">
                              <Typography className="text-[16px] left-[20px]">Qty</Typography>
                            </Table.Cell>
                            <Table.Cell className="w-[15%]">
                              <Typography className="text-[16px]">Code</Typography>
                            </Table.Cell>
                            <Table.Cell className="w-[20%]">
                              <Typography className="text-[16px]">Price</Typography>
                            </Table.Cell>
                          </Table.Row>
                        </Table.Head>

                        <Table.Body>
                          <SortableContext
                            items={section.items.filter((item) => item.isProductAvailable).map((item) => item.id)}
                            strategy={verticalListSortingStrategy}
                          >
                            {section.items
                              .filter((item) => item.isProductAvailable)
                              .map((item, index) => (
                                <SortableRow
                                  key={item.id}
                                  item={item}
                                  index={index}
                                  sectionIndex={sectionIndex}
                                  ItemQuantity={ItemQuantity}
                                  handleQuantityChange={handleQuantityChange}
                                  PriceDetails={PriceDetails}
                                  OpenDialogButton={OpenDialogButton}
                                  onUpdateNotes={onUpdateNotes}
                                  currentlyEditingId={currentlyEditingId}
                                  setCurrentlyEditingId={setCurrentlyEditingId}
                                  setIsFormDirty={setIsFormDirty}
                                  isFormDirtyRef={isFormDirtyRef}
                                  setPendingNotesData={setPendingNotesData}
                                  sections={sections}
                                  initialQuantitiesRef={initialQuantitiesRef}
                                  lineItemsToRemove={lineItemsToRemove}
                                  projectFormDataState={projectFormDataState}
                                  lineItemsPriorityToUpdate={lineItemsPriorityToUpdate}
                                />
                              ))}
                          </SortableContext>

                          {section.items.map((item, index) =>
                            !item.isProductAvailable ? (
                              <Table.Row
                                key={`unavailable-${index}`}
                                className={`${index % 2 === 1 ? 'bg-gray-100' : ''} w-full shadow-none`}
                              >
                                <td colSpan={4} className="p-4">
                                  <div className="flex flex-col md:flex-row w-full md:items-center justify-between gap-4">
                                    <div className="flex items-center space-x-2">
                                      <InformationCircleIcon className="h-[24px] w-[24px] stroke-[#F27320] text-[#F27320]" />
                                     <div className='ml-[10px]'>
<Typography className=" text-base text-[#F27320] whitespace-break-spaces">
                                        This product you have selected may no longer be available.
                                      </Typography>
                                    Code: <span className='font-semibold'>{item?.variant?.attributes?.['item-code']}</span>

                                     </div>
                                      
                                    </div>
                                    <Button onClick={handleButtonClick} className="!bg-[#A6C9CC] text-black cursor-pointer flex-shrink-0 whitespace-nowrap">
                                      View recommended alternatives
                                    </Button>
                                  </div>
                                </td>
                                <Table.Cell className="w-[20%] p-4 text-black align-middle">
                                  <div className="flex justify-end items-center h-full">
                                    <OpenDialogButton item={item} sectionIndex={sectionIndex} identifier="lineItem" />
                                  </div>
                                </Table.Cell>
                              </Table.Row>
                            ) : null,
                          )}
                        </Table.Body>
                      </Table.Container>
                    </DndContext>
                  )}

                  {!isDesktop && (
                    <DndContext
                      sensors={mobileSensors}
                      collisionDetection={closestCenter}
                      onDragStart={() => lockScroll()}
                      onDragEnd={handleDragEnd}
                      modifiers={[restrictToVerticalAxis, restrictToFirstScrollableAncestor]}
                    >
                      <div>
                        <SortableContext
                          items={section.items.filter((item) => item.isProductAvailable).map((item) => item.id)}
                          strategy={verticalListSortingStrategy}
                        >
                          {section.items
                            .filter((item) => item.isProductAvailable)
                            .map((item, index) => (
                              <SortableRowSm
                                key={item.id}
                                item={item}
                                index={index}
                                sectionIndex={sectionIndex}
                                ItemQuantity={ItemQuantity}
                                handleQuantityChange={handleQuantityChange}
                                PriceDetails={PriceDetails}
                                OpenDialogButton={OpenDialogButton}
                                onUpdateNotes={onUpdateNotes}
                                currentlyEditingId={currentlyEditingId}
                                setCurrentlyEditingId={setCurrentlyEditingId}
                                setIsFormDirty={setIsFormDirty}
                                isFormDirtyRef={isFormDirtyRef}
                                setPendingNotesData={setPendingNotesData}
                                sections={sections}
                                initialQuantitiesRef={initialQuantitiesRef}
                                lineItemsToRemove={lineItemsToRemove}
                                projectFormDataState={projectFormDataState}
                                lineItemsPriorityToUpdate={lineItemsPriorityToUpdate}
                              />
                            ))}
                        </SortableContext>
                        {section.items.map((item, index) =>
                          !item.isProductAvailable ? (
                            <div
                              key={`unavailable-${index}`}
                              className={`${index % 2 === 1 ? 'bg-gray-100' : ''} w-full shadow-none`}
                            >
                              <div>
                                <div className="flex w-full justify-between items-start p-2">
                                  <div className="grid w-full">
                                    <div className="mb-3 flex items-start space-x-2">
                                      <InformationCircleIcon className="max-w-64 h-[24px] max-h-64  w-[24px] flex-shrink-0 stroke-[#F27320] text-[#F27320]" />
                                      <div className='ml-[10px]'>
                                        <Typography className=" text-base text-[#F27320]">
                                          This product you have selected may no longer be available.
                                        </Typography>
                                        <span className='font-semibold'>{item?.variant?.attributes?.['item-code']}</span>
                                      </div>
                                    </div>
                                    <Button onClick={() => handleButtonClick()} className="!bg-[#A6C9CC] text-black w-fit">
                                      View recommended alternatives
                                    </Button>
                                  </div>
                                  <div className="flex-shrink-0 ml-[10px]">
                                    <OpenDialogButton item={item} sectionIndex={sectionIndex} identifier="lineItem" />
                                  </div>
                                </div>
                              </div>
                            </div>
                          ) : null,
                        )}
                      </div>
                    </DndContext>
                  )}
                </div>
              </div>
            );
          })}
        </>
      </div>
      <EditConfirmationPopup
        isOpen={showConfirmPopup}
        selectedRoom={{ name: editedRoomName }}
        onConfirm={handleConfirmUpdate}
        onCancel={() => setShowConfirmPopup(false)}
        isLoading={isUpdating}
        successMessage={updateSuccessMessage}
        roomAlreadyExists={roomAlreadyExists}
      />


      <div className="popup-container">
        <ConfirmationPopup isOpen={openPopup} selectedRoom={selectedItem} identifier={identifier} />
      </div>
      <div className="savepopup-container">
        <SaveConfirmationPopup
          isOpen={openSaveConfirmation}
          handleDiscard={discardConfirmation}
          handleSaveNContinue={handleSaveAndContinue}
          handleClose={closeConfirmation}
        />
      </div>
      <div className="flex flex-col justify-between md:flex-row mt-[10px]">
        <div className="mb-[4px] md:!mb-0">
          {isDesktop && <Button className='cursor-pointer !bg-[#1A1A1A] hover:!bg-gray-500 !py-[12px] !px-[24px]' onClick={() => navigationToDiffStepper(2)}>Back to products</Button>}
          {!isDesktop && (
            <Button className="w-full cursor-pointer !bg-[#1A1A1A] hover:!bg-gray-500 !py-[12px] !px-[24px]" onClick={() => handleSubmit(4)}>
              Save & Continue
            </Button>
          )}
        </div>
        <div className="flex justify-between md:!mb-0 md:justify-end">
          {!isDesktop && <Button className='cursor-pointer !bg-[#1A1A1A] hover:!bg-gray-500 !py-[12px] !px-[24px]' onClick={() => navigationToDiffStepper(2)}>Back to products</Button>}
          {isDesktop && (
            <Button className="w-full cursor-pointer !bg-[#1A1A1A] hover:!bg-gray-500 !py-[12px] !px-[24px]" onClick={() => handleSubmit(4)}>
              Save & Continue
            </Button>
          )}
          <div className="ml-[15px]">
            <Button className="md:min-w-[150px] cursor-pointer !bg-[#1A1A1A] hover:!bg-gray-500 !py-[12px] !px-[24px]" onClick={() => navigationToDiffStepper(4)}>
              Go to Summary
            </Button>
          </div>
        </div>
      </div>
      {loading && <Spinner />}
            {loading && <div className="overlay fixed left-0 top-0 z-50 h-full w-full bg-gray-800 opacity-75"></div>}
</div>
  );
};

export default ViewProducts;

const SortableRow = ({ item, index, sectionIndex, ItemQuantity, PriceDetails, OpenDialogButton, onUpdateNotes, currentlyEditingId,
  setCurrentlyEditingId, handleQuantityChange, setIsFormDirty, isFormDirtyRef, setPendingNotesData, sections, initialQuantitiesRef,
  lineItemsToRemove, projectFormDataState, lineItemsPriorityToUpdate }: any) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: item.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    cursor: 'grab',
    opacity: isDragging ? 0.6 : 1,
    backgroundColor: isDragging ? '#f3f4f6' : index % 2 === 1 ? '#e5e7eb' : 'white',
    zIndex: isDragging ? 999 : 'auto',
    position: (isDragging ? 'relative' : 'static') as 'relative' | 'absolute' | 'fixed' | 'static' | 'sticky',
  };

  const editing = currentlyEditingId === item.id;
  const [notes, setNotes] = useState(item.roomNotes || "");

  const hasOtherChanges = () => {
    const changedItems: { lineItemId: string; quantity: number; }[] = [];
    const initialQuantities = initialQuantitiesRef.current;

    sections.forEach((section: any) => {
      section.items.forEach((itm: any) => {
        const initialQty = initialQuantities.get(itm.id);
        if (initialQty !== undefined && initialQty !== itm.quantity) {
          changedItems.push({
            lineItemId: itm.id,
            quantity: itm.quantity
          });
        }
      });
    });

    const hasQuantityChanges = changedItems.length > 0;
    const hasLineItemRemovals = lineItemsToRemove.length > 0;
    const hasRoomRemovals = projectFormDataState?.roomGroups?.length !== sections.length;
    const hasPriorityUpdates = lineItemsPriorityToUpdate?.length > 0;

    return hasQuantityChanges || hasLineItemRemovals || hasRoomRemovals || hasPriorityUpdates;
  };

  const handleRoomNotesClick = (e: any) => {
    setCurrentlyEditingId(item.id);
  };
  const handleNotesChange = (e: any) => {
    const newValue = e.target.value;
    setNotes(newValue);
    if (newValue !== item.roomNotes) {
      setIsFormDirty(true);
      isFormDirtyRef.current = true;
      // Track pending notes data for save confirmation
      setPendingNotesData({notes: newValue, item, sectionIndex});
    }
  };
  const handleSuccessEditNotes = () => {
    onUpdateNotes(notes, item);
    setCurrentlyEditingId(null);
    setPendingNotesData(null);
    // Only reset dirty state if there are no other changes
    if (!hasOtherChanges()) {
      setIsFormDirty(false);
      isFormDirtyRef.current = false;
    }
  };
  const closeEditNotes = () => {
    setNotes(item.roomNotes || "");
    setCurrentlyEditingId(null);
    // Clear pending notes since edit was cancelled
    setPendingNotesData(null);
    // Only reset dirty state if there are no other changes
    if (!hasOtherChanges()) {
      setIsFormDirty(false);
      isFormDirtyRef.current = false;
    }
  };
const lifecycle = item.variant?.attributes?.['item-attributes']?.find(
  (m: any) => m.name === 'lifecycle'
)?.value?.['en-AU'];

const badge = getLifecycleBadge(lifecycle);
  return (
    <Table.Row ref={setNodeRef} style={style} {...attributes} {...listeners} className={`mb-18px w-full`}>
      <Table.Cell className="w-[10%] overflow-hidden !p-0 !pb-[1px] text-black">
        {(() => {
          const lifecycle = item.variant?.attributes?.['item-attributes']?.find(
            (m: any) => m.name === 'lifecycle'
          )?.value?.['en-AU'];

          const badge = getLifecycleBadge(lifecycle);

          return (
            <div className="relative inline-block">
              {badge && (
                <span
                  className="absolute left-1 top-1 z-10 rounded-[3px] px-0.5 py-[2px] text-[5px] font-normal uppercase leading-none text-white"
                  style={{ backgroundColor: badge.color }}
                >
                  {badge.label}
                </span>
              )}

              <img
                src={item.variant.images[0] ?? '/images/no-image-placeholder/product-thumbnail.jpg'}
                alt={item.variant.attributes['item-name']}
                className="h-[80px] w-[80px] object-cover"
              />
            </div>
          );
        })()}
      </Table.Cell>

                      <Table.Cell className="w-[40%] overflow-hidden p-[1px] text-black">
        <div className="flex items-center">
          <div>
            <p className="whitespace-normal flex gap-[5px] break-words text-[16px] font-semibold overflow-hidden"
              >
              {item.variant.attributes["item-name"]}
              <span
                data-tooltip-id="add-note-tooltip"
                data-tooltip-content="Add a note"
              >
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor"
                  className="h-[20px] w-[20px] cursor-pointer"
                  onClick={(e) => {
                    e.preventDefault();
                    handleRoomNotesClick(e)
                  }}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    handleRoomNotesClick(e)
                  }}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="m16.862 4.487 1.687-1.688a1.875 1.875 0
                   1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0
                   0L19.5 7.125M18 14v4.75A2.25 2.25 0 0 1 15.75 21H5.25A2.25 2.25 0 0 1 3 18.75V8.25A2.25 2.25 0 0 1 5.25 6H10" />
                </svg>
              </span>
              <Tooltip id="add-note-tooltip" place="top" style={{ fontSize: '14px' }} />
            </p>
            {item.variant.attributes.colour ? (
              <Typography className="whitespace-normal break-words text-[14px]">{`Colour: ${item.variant.attributes.colour}`}</Typography>
            ) : null}

            {editing ? (
              <div className='flex items-center gap-[10px]'
                onClick={(e) => e.stopPropagation()}
                onPointerDown={(e) => e.stopPropagation()}>
                <textarea
                  className="w-full mt-[2px] p-[2px] border rounded text-[14px]"
                  value={notes}
                  onChange={handleNotesChange}
                  autoFocus
                  maxLength={300}
                />
                <svg
                  onClick={handleSuccessEditNotes}
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                  className="h-[25px] w-[25px] cursor-pointer text-green-600"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="m4.5 12.75 6 6 9-13.5" />
                </svg>
                <svg
                  onClick={closeEditNotes}
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                  className="h-[25px] w-[25px] cursor-pointer text-red-500"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
                </svg>
              </div>
            ) : (<>
              {notes?.length > 0 ? (
                <div className='flex'>
                  <p>Notes: </p><span className='mt-[5px] whitespace-normal flex gap-[5px] break-words text-[14px]'>{notes}</span>
                </div>
              ) : ('')}
            </>)}

          </div>
        </div>
      </Table.Cell>
                      <Table.Cell className="w-[15%] !p-0 break-words text-black">
        <div className="flex items-center space-x-2">
          <ItemQuantity item={item} sectionIndex={sectionIndex} handleQuantityChange={handleQuantityChange}/>
        </div>
      </Table.Cell>
                      <Table.Cell className="w-[15%] break-words text-black">
        <Typography className="whitespace-normal text-[16px]">{item.variant.attributes['item-code']}</Typography>
      </Table.Cell>
                      <Table.Cell className="w-[20%] break-words text-black">
        <div className="flex items-center justify-between">
  <div
    className={
      item.variant?.attributes?.['item-attributes']?.find(
        (m: any) => m.name === 'lifecycle'
      )?.value?.['en-AU'] === 'PROTOTYPE'
        ? 'invisible'
        : ''
    }
  >
    <PriceDetails item={item} />
  </div>

  <OpenDialogButton
    item={item}
    sectionIndex={sectionIndex}
    identifier="lineItem"
  />
</div>
      </Table.Cell>
    </Table.Row>
  );
};

const SortableRowSm = ({ item, index, sectionIndex, ItemQuantity, PriceDetails, OpenDialogButton, onUpdateNotes, currentlyEditingId,
  setCurrentlyEditingId, handleQuantityChange, setIsFormDirty, isFormDirtyRef, setPendingNotesData, sections, initialQuantitiesRef,
  lineItemsToRemove, projectFormDataState, lineItemsPriorityToUpdate }: any) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: item.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    cursor: 'grab',
    opacity: isDragging ? 0.6 : 1,
    backgroundColor: isDragging ? '#f3f4f6' : index % 2 === 1 ? '#e5e7eb' : 'white',
    zIndex: isDragging ? 999 : 'auto',
    position: (isDragging ? 'relative' : 'static') as 'relative' | 'absolute' | 'fixed' | 'static' | 'sticky',
    touchAction: isDragging ? 'none' : 'auto',

  };

  const editing = currentlyEditingId === item.id;
  const [notes, setNotes] = useState(item.roomNotes || "");

  // Helper function to check if there are other changes beyond notes
  const hasOtherChanges = () => {
    // Check quantity changes
    const changedItems: { lineItemId: string; quantity: number; }[] = [];
    const initialQuantities = initialQuantitiesRef.current;

    sections.forEach((section: any) => {
      section.items.forEach((itm: any) => {
        const initialQty = initialQuantities.get(itm.id);
        if (initialQty !== undefined && initialQty !== itm.quantity) {
          changedItems.push({
            lineItemId: itm.id,
            quantity: itm.quantity
          });
        }
      });
    });

    const hasQuantityChanges = changedItems.length > 0;
    const hasLineItemRemovals = lineItemsToRemove.length > 0;
    const hasRoomRemovals = projectFormDataState?.roomGroups?.length !== sections.length;
    const hasPriorityUpdates = lineItemsPriorityToUpdate?.length > 0;

    return hasQuantityChanges || hasLineItemRemovals || hasRoomRemovals || hasPriorityUpdates;
  };

  const handleRoomNotesClick = (e: any) => {
    e.stopPropagation();
    setCurrentlyEditingId(item.id);
  };
  const handleNotesChange = (e: any) => {
    const newValue = e.target.value;
    setNotes(newValue);
    if (newValue !== item.roomNotes) {
      setIsFormDirty(true);
      isFormDirtyRef.current = true;
      setPendingNotesData({notes: newValue, item, sectionIndex});
    }
  };
  const handleSuccessEditNotes = () => {
    onUpdateNotes(notes, item);
    setCurrentlyEditingId(null);
    setPendingNotesData(null);
    if (!hasOtherChanges()) {
      setIsFormDirty(false);
      isFormDirtyRef.current = false;
    }
  };
  const closeEditNotes = () => {
    setNotes(item.roomNotes || "");
    setCurrentlyEditingId(null);
    setPendingNotesData(null);
    if (!hasOtherChanges()) {
      setIsFormDirty(false);
      isFormDirtyRef.current = false;
    }
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className={`${index % 2 === 1 ? 'bg-gray-200' : ''}  ${!item.isProductAvailable ? 'opacity-50' : ''
      } mb-18px w-full`}
    >
      <div className="p-[1px]">
        <div className="flex items-start">
          {(() => {
            const lifecycle = item.variant?.attributes?.['item-attributes']?.find(
              (m: any) => m.name === 'lifecycle'
            )?.value?.['en-AU']; // or [localeKey]

            const badge = getLifecycleBadge(lifecycle);

            return (
              <div className="relative">
                {badge && (
                  <span
                    className="absolute left-1 top-1 z-10 rounded-[3px] px-0.5 py-[2px] text-[5px] font-normal uppercase leading-none text-white"
                    style={{ backgroundColor: badge.color }}
                  >
                    {badge.label}
                  </span>
                )}

                <img
                  src={item.variant.images[0] ?? '/images/no-image-placeholder/product-thumbnail.jpg'}
                  alt={item.variant.attributes['item-name']}
                  className="h-[80px] w-[80px] object-cover"
                />
              </div>
            );
          })()}
          <div className="ml-[16px] flex w-full justify-between gap-[10px]">
            <div className='flex gap-[10px]'>
              <Typography className="text-[16px] font-semibold">{item.variant.attributes["item-name"]}</Typography>
              <span>
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor"
                  className="h-[20px] w-[20px] cursor-pointer"
                  onClick={handleRoomNotesClick}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="m16.862 4.487 1.687-1.688a1.875 1.875 0
                   1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0
                   0L19.5 7.125M18 14v4.75A2.25 2.25 0 0 1 15.75 21H5.25A2.25 2.25 0 0 1 3 18.75V8.25A2.25 2.25 0 0 1 5.25 6H10" />
                </svg>
              </span>
            </div>
            <div className="flex-shrink-0">
              <OpenDialogButton item={item} sectionIndex={sectionIndex} identifier="lineItem" />
            </div>
          </div>
        </div>

        <div className="ml-[96px] flex items-center justify-between">
          {item.variant.attributes.colour ? (
            <Typography className="whitespace-normal break-words text-[12px]">{`Colour: ${item.variant.attributes.colour}`}</Typography>
          ) : <div />}
          <Typography className="whitespace-normal break-words text-[12px]">
            {item.variant.attributes['item-code']}
          </Typography>
        </div>
        <div className="ml-[96px] items-center justify-between">
          {editing ? (
            <div className='flex items-center gap-[10px]'
              onClick={(e) => e.stopPropagation()}
              onPointerDown={(e) => e.stopPropagation()}>
              <textarea
                className="w-full mt-[2px] p-[2px] border rounded text-[14px]"
                value={notes}
                onChange={handleNotesChange}
                autoFocus
              />
              <svg
                onClick={handleSuccessEditNotes}
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
                                        className="h-[25px] w-[25px] cursor-pointer text-green-600"
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="m4.5 12.75 6 6 9-13.5" />
              </svg>
              <svg
                onClick={closeEditNotes}
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
                                        className="h-[25px] w-[25px] cursor-pointer text-red-500"
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
              </svg>
            </div>
          ) : (
            <>
              {notes?.length > 0 ? (
                <div className='flex'>
                  <p>Notes: </p><span className='mt-[5px] whitespace-normal flex gap-[5px] break-words text-[14px]'>{notes}</span>
                </div>
              ) : ('')}
            </>
          )}
        </div>

        <div className="ml-[96px] xs:flex gap-1.25 mb-1.25 flex-col">
          <div
            className={
              item.variant?.attributes?.['item-attributes']?.find(
                (m: any) => m.name === 'lifecycle'
              )?.value?.['en-AU'] === 'PROTOTYPE'
                ? 'invisible'
                : ''
            }
          >
            <PriceDetails item={item} />
          </div>
          <div className="flex items-center space-x-[2px]">
            <ItemQuantity item={item} sectionIndex={sectionIndex} handleQuantityChange={handleQuantityChange}/>
          </div>
        </div>
      </div>
    </div>
  );
};

const ItemQuantity: React.FC<ItemQuantityProps> = memo(function ItemQtyComp({ item, sectionIndex, handleQuantityChange }) {
    const [localQuantity, setLocalQuantity] = useState(item.quantity);
    const inputWidth = `${Math.max(String(item.quantity).length, 7)}ch`;

    useEffect(() => {
        setLocalQuantity(item.quantity);
    }, [item.quantity]);


    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        if (value === '') {
            setLocalQuantity(NaN);
            return;
        }

        const parsed = parseInt(value, 10);
        if (!isNaN(parsed) && parsed <= 9999) {
            setLocalQuantity(parsed);
        }
    };

    const commitQuantity = (newQuantity: number) => {
        const quantityToCommit = isNaN(newQuantity) || newQuantity < 1 ? 1 : newQuantity;
        handleQuantityChange(sectionIndex, item.id, 'set', quantityToCommit);
    };
    return (
        <div className="flex items-center gap-[5px]"
            onClick={(e) => e.stopPropagation()}
            onPointerDown={(e) => e.stopPropagation()}
        >
            <Button
                variant='ghost'
                className={`!p-0 text-[25px] w-[24px] h-[24px] ${!item.isProductAvailable ? 'pointer-events-none opacity-50' : 'cursor-pointer'}`}
                onMouseDown={(e) => {
                    e.preventDefault();
                    commitQuantity((localQuantity || 1) - 1);

                }}
                disabled={item.quantity <= 1}
            >
                -
            </Button>
            <input
                type="number"
                className="text-center border border-gray-300 rounded px-[2px] py-[1px] text-[16px]"
                style={{ width: inputWidth }}
                value={localQuantity}
                min={1}
                max={9999}
                onChange={handleChange}
                onBlur={() => commitQuantity(localQuantity)}
                onFocus={(e) => e.target.select()}
                disabled={!item.isProductAvailable}
            />
            <Button
                variant='ghost'
                className={`!p-0 text-[16px] w-[24px] h-[24px] ${!item.isProductAvailable || item.quantity >= 9999
                    ? 'pointer-events-none opacity-50'
                    : 'cursor-pointer'
                    }`}
                onMouseDown={(e) => {
                    e.preventDefault();
                    commitQuantity((localQuantity || 0) + 1);
                }}
            >
                +
            </Button>
        </div>
    );
});
